import dayjs from 'dayjs'
import { generatePath, useHistory } from 'react-router-dom'
import { Modal, useModals } from 'almanac-ui/Modal'
import { CONTENT_PLAN_PATH } from 'paths'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCampaignOptions } from 'hooks/queries/campaignOptions/useCampaignOptions'
import { collectResults, getTotalCountFromInfiniteQuery } from 'utils/query'
import { NoCampaignModal } from '../NoCampaignModal/NoCampaignModal'
import { ComposeStep } from './ComposeStep'
import { Skeleton } from 'components/Skeleton/Skeleton'
import Styles from './ComposeRow.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { CampaignChipDropdown } from './CampaignChipDropdown'
import { FilePlus1, Plus } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { ReferenceImageTrigger } from 'screens/Schedule/PostPromptsSection/ReferenceImageTrigger'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { noop } from 'lodash'
import { ContentTypeSelect } from 'screens/Schedule/PostPromptsSection/ContentTypeSelect'
import { CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY } from 'screens/Schedule/PostPromptsSection/utils'
import { Text } from 'blaze-ui/Text/Text'
import { CampaignOption } from 'types'
import { ContentCategory } from 'utils/brandPlans/contentCategories'
import { CreateCampaignModalV2 } from 'screens/ContentPlan/CreateCampaignModalV2/CreateCampaignModalV2'

interface NewPostModalProps {
  initialScheduleId?: number
  initialContentCategory?: ContentCategory
  onSuccess?: (postsCount: number, brandPlanScheduleId: number) => void
  scheduleSelectionEnabled?: boolean
}

export const NewPostModal = ({
  initialScheduleId,
  initialContentCategory,
  onSuccess,
  scheduleSelectionEnabled = true,
}: NewPostModalProps) => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const { openModal, closeModal, closeAllModals } = useModals()
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()
  const schedulesQuery = useCampaignOptions(
    {
      brandPlanId: brandPlan?.id ?? 0,
      start_date: dayjs().format('YYYY-MM-DD'),
      order_direction: 'asc',
      items: 20,
    },
    { enabled: !!brandPlan?.id },
  )
  const schedules = collectResults(schedulesQuery, (d) => d.campaign_options).filter(Boolean)
  const noCampaignsLoading = schedulesQuery.isLoading
  const hasNoCampaigns = schedulesQuery.isFetched && getTotalCountFromInfiniteQuery(schedulesQuery) === 0
  const oneOffScheduleId = brandPlan?.one_off_schedule_id

  if (!brandPlan || noCampaignsLoading) {
    return (
      <Modal.Root size="md">
        <Modal.Header
          subHeader="Create one or more posts from scratch or with AI. AI generated posts will auto suggest a topic from your campaign and select a reference image."
          subHeaderGap={12}
        >
          <div className={Styles.headerRow}>
            <Heading level={2}>New Post</Heading>
            {scheduleSelectionEnabled && (
              <CampaignChipDropdown
                schedules={schedules}
                selectedSchedule={schedules[0]}
                isLoading
                onSelect={() => {}}
              />
            )}
          </div>
        </Modal.Header>
        <Modal.Content>
          <div className={Styles.composeList}>
            <div className={Styles.row}>
              <div className={Styles.mediaSlot}>
                <ReferenceImageTrigger onUse={() => {}} mediaTypes={[]} options={{ newStack: true }}>
                  <LoadingShimmerer borderRadius={10} />
                </ReferenceImageTrigger>
              </div>

              <div className={Styles.body}>
                <div className={Styles.topic}>
                  <Skeleton active paragraph={{ rows: 3 }} title={false} className={Styles.topicSkeleton} />
                </div>
                <div className={Styles.controlsRow}>
                  <div className={Styles.controlsLeft}>
                    <ContentTypeSelect
                      selectedKey={
                        CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY[initialContentCategory ?? 'still_image_post']
                      }
                      isDisabled
                      variant="secondary"
                      size="sm"
                      onSelectionChange={noop}
                    />
                    <Text variant="secondary" color="var(--dark-60)">
                      Posting on
                    </Text>
                    <DateTimePicker dateTime={dayjs()} setDateTime={noop}>
                      <Button withChevron="down" variant="secondary" size="sm" isDisabled>
                        {dayjs().format('MMM D, h:mma')}
                      </Button>
                    </DateTimePicker>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <div className={Styles.addMoreRow}>
              <Button variant="secondary" frontIcon={Plus} size="lg" isDisabled>
                New Post
              </Button>
              <Button variant="tertiary" frontIcon={FilePlus1} size="lg" isDisabled>
                Blank Post
              </Button>
            </div>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right" className={Styles.footerRight}>
            <Button variant="primary" size="lg" isDisabled>
              Schedule 1 Post
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )
  }

  if (hasNoCampaigns && oneOffScheduleId === undefined) {
    return (
      <NoCampaignModal
        onCancel={() => closeModal()}
        onCreateCampaign={() => {
          closeModal()
          openModal(CreateCampaignModalV2, {
            brandPlan,
            onSuccess: () => {
              history.push(generatePath(CONTENT_PLAN_PATH, { workspaceId }))
            },
          })
        }}
      />
    )
  }

  const fallbackSchedule: CampaignOption | undefined =
    oneOffScheduleId !== undefined
      ? {
          id: oneOffScheduleId,
          title: undefined,
          start_date: dayjs().format('YYYY-MM-DD'),
          end_date: dayjs().format('YYYY-MM-DD'),
          week_days: [],
          tactics: [],
          cover_image: undefined,
          kind: 'one_off',
        }
      : undefined

  const selectedSchedule = schedules.find((s) => s.id === initialScheduleId) ?? schedules[0] ?? fallbackSchedule

  const handlePostsCreated = (postsCount: number, brandPlanScheduleId: number) => {
    onSuccess?.(postsCount, brandPlanScheduleId)
    closeAllModals()
  }

  return (
    <Modal.Root size="md">
      {selectedSchedule && (
        <ComposeStep
          brandPlan={brandPlan}
          initialSchedule={selectedSchedule}
          schedules={schedules}
          isLoadingSchedules={schedulesQuery.isLoading}
          workspaceId={workspaceId}
          onPostsCreated={handlePostsCreated}
          oneOffScheduleId={oneOffScheduleId}
          initialContentCategory={initialContentCategory}
          scheduleSelectionEnabled={scheduleSelectionEnabled}
        />
      )}
    </Modal.Root>
  )
}
